import classNames from "classnames";
import React from "react";
import {
  CaregiverSurveyQuestionAnswer,
  CaregiverSurveyQuestionWithPossibleAnswers,
} from "../../../messages";
import {
  CaregiverSurveyQuestionAnswerId,
  CaregiverSurveyQuestionId,
} from "../../../messages/ObjectIds";
import { OrientationLanguages } from "../../../messages/Video";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { AppButton } from "../../Generic/AppButton";
import { AppListItem } from "../../Generic/AppList";
import AppSlideFadeIn from "../../Generic/AppSlideFadeIn";
import Flex from "../../Generic/Flex";
import NextButton from "../../NextButton/NextButton";
import CaregiverSurveyProgress from "../CaregiverSurveyProgress/CaregiverSurveyProgress";

interface Props {
  question: CaregiverSurveyQuestionWithPossibleAnswers;
  questionsPercentage: string;
  questionsLeftText: string | null;
  language: OrientationLanguages;
  onSubmitAnswer: (
    questionId: CaregiverSurveyQuestionId,
    answerIds: CaregiverSurveyQuestionAnswerId[]
  ) => void;
}

const useCaregiverSurveyQuestion = (props: Pick<Props, "question" | "onSubmitAnswer">) => {
  const [selected, setSelected] = React.useState<CaregiverSurveyQuestionAnswerId[]>([]);

  const isSelected = ({ id }: CaregiverSurveyQuestionAnswer) => {
    return selected.indexOf(id) >= 0;
  };

  const answerSingleAnswer = (answer: CaregiverSurveyQuestionAnswer) => {
    return props.onSubmitAnswer(props.question.id, [answer.id]);
  };

  const answerMultiAnswers = (answer: CaregiverSurveyQuestionAnswer) => {
    return setSelected((prev) =>
      isSelected(answer) ? prev.filter((x) => x !== answer.id) : [...prev, answer.id]
    );
  };

  const submitMultiAnswers = () => props.onSubmitAnswer(props.question.id, selected);

  return {
    state: { selected: selected },
    actions: {
      answerSingleAnswer: answerSingleAnswer,
      answerMultiAnswers: answerMultiAnswers,
      submitMultiAnswers: submitMultiAnswers,
    },
    meta: {
      isSelected: isSelected,
    },
  };
};

const CaregiverSurveyQuestion = (props: Props) => {
  const { state, meta, actions } = useCaregiverSurveyQuestion({
    question: props.question,
    onSubmitAnswer: props.onSubmitAnswer,
  });

  const handleClickAnswer = (answer: CaregiverSurveyQuestionAnswer) => {
    props.question.isMulti
      ? actions.answerMultiAnswers(answer)
      : actions.answerSingleAnswer(answer);
  };

  return (
    <div className="CaregiverSurveyQuestion">
      <AppSlideFadeIn>
        <div className="content">
          <div className="header">
            <h3>{props.question.title[props.language]}</h3>
          </div>
          <div className="questionsContainer">
            {props.question.possibleAnswers.map((answer) => {
              return (
                <AppListItem
                  key={CaregiverSurveyQuestionAnswerId.unwrap(answer.id)}
                  title={answer.text[props.language]}
                  trailing={<NextButton />}
                  className={classNames({ selected: meta.isSelected(answer) })}
                  onClick={() => handleClickAnswer(answer)}
                />
              );
            })}
          </div>

          {props.question.isMulti ? (
            <Flex
              className="questionsSubmit"
              stretchItems={true}
              onClick={actions.submitMultiAnswers}
            >
              <AppButton variant="filled" disabled={state.selected.length === 0}>
                {translate(STRINGS.NEXT)}
              </AppButton>
            </Flex>
          ) : null}
        </div>
      </AppSlideFadeIn>
      <CaregiverSurveyProgress
        progressPerecentage={props.questionsPercentage}
        completionText={props.questionsLeftText}
      />
    </div>
  );
};

export default React.memo(CaregiverSurveyQuestion);
